"use client";

import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { ArrowDown, ArrowUpRight, MoveUpRight, Pause, Play, RotateCcw, X } from "lucide-react";
import type { HomeContent } from "@/shared/config/home";
import styles from "./hero.module.css";
import { WalkJoystick } from "./walk-joystick";

type Office = { enter(): void; exit(): void; move(x: number, z: number): void; reset(): void; pause(value: boolean): void; destroy(): void };

export function Hero({ content }: { content: HomeContent["hero"] }) {
  const host = useRef<HTMLDivElement>(null);
  const office = useRef<Office | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [demo, setDemo] = useState(false);
  const [paused, setPaused] = useState(false);
  const [retry, setRetry] = useState(0);
  const savedScroll = useRef(0);
  const ownsHistory = useRef(false);
  const leaving = useRef(false);

  const leave = useCallback(() => {
    if (leaving.current) return;
    leaving.current = true;
    if (ownsHistory.current && location.hash === "#demo") history.back();
    else { history.replaceState(history.state, "", location.pathname + location.search); setDemo(false); }
  }, []);

  useEffect(() => {
    const abort = new AbortController();
    let instance: Office | undefined;
    const timeout = setTimeout(() => { if (!abort.signal.aborted) setFailed(true); }, 45000);
    void (async () => {
      try {
        const url = new URL("/office/experience.js?v=20261103-8", location.origin).href;
        const { mountOffice } = await import(/* webpackIgnore: true */ /* turbopackIgnore: true */ url);
        if (abort.signal.aborted || !host.current) return;
        instance = await mountOffice(host.current, { signal: abort.signal,
          onVisible: () => { if (!abort.signal.aborted) setVisible(true); },
          onProgress: (value: number) => { if (!abort.signal.aborted) setProgress(value); },
          onReady: () => { clearTimeout(timeout); if (!abort.signal.aborted) { setReady(true); setFailed(false); window.dispatchEvent(new Event("seero:office-ready")); if (location.hash === "#demo") setDemo(true); } },
          onError: () => { clearTimeout(timeout); if (!abort.signal.aborted) setFailed(true); },
        });
        if (abort.signal.aborted) { instance?.destroy(); return; }
        office.current = instance || null;
      } catch { if (!abort.signal.aborted) setFailed(true); }
    })();
    return () => { clearTimeout(timeout); abort.abort(); instance?.destroy(); office.current = null; };
  }, [retry]);

  useEffect(() => {
    const pop = () => setDemo(location.hash === "#demo");
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, []);

  useEffect(() => {
    if (!demo) return;
    leaving.current = false;
    const returnFocus = trigger.current;
    savedScroll.current = window.scrollY;
    document.body.dataset.officeDemo = "true";
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const otherElements = [...document.querySelectorAll<HTMLElement>("body > header, body > footer, #main > :not(#top)")];
    const previousInert = otherElements.map(el => el.inert);
    otherElements.forEach(el => { el.inert = true; });
    office.current?.enter();
    closeButton.current?.focus({ preventScroll: true });
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); leave(); }
      if (event.key === "Tab") {
        const focusable = [...document.querySelectorAll<HTMLElement>(`[data-office-controls] button`)].filter(el => el.getClientRects().length > 0);
        const index = focusable.indexOf(document.activeElement as HTMLElement);
        event.preventDefault(); focusable[(index + (event.shiftKey ? -1 : 1) + focusable.length) % focusable.length]?.focus();
      }
    };
    // Native Escape may release pointer lock without delivering a keydown to the page.
    let hadPointerLock = !!host.current?.contains(document.pointerLockElement);
    const pointerLockChanged = () => {
      const hasPointerLock = !!host.current?.contains(document.pointerLockElement);
      if (hadPointerLock && !hasPointerLock) leave();
      hadPointerLock = hasPointerLock;
    };
    document.addEventListener("pointerlockchange", pointerLockChanged);
    window.addEventListener("keydown", keydown, true);
    return () => {
      document.removeEventListener("pointerlockchange", pointerLockChanged);
      window.removeEventListener("keydown", keydown, true);
      office.current?.exit();
      delete document.body.dataset.officeDemo;
      document.body.style.overflow = previousOverflow;
      otherElements.forEach((el, index) => { el.inert = previousInert[index]; });
      window.scrollTo({ top: savedScroll.current, behavior: "instant" });
      returnFocus?.focus({ preventScroll: true });
    };
  }, [demo, leave]);

  const enter = () => {
    if (!ready) return;
    history.pushState({ ...history.state, officeDemo: true }, "", "#demo");
    ownsHistory.current = true;
    setDemo(true);
  };

  useEffect(() => {
    const open = () => {
      if (!ready || location.hash === "#demo") return;
      history.pushState({ ...history.state, officeDemo: true }, "", "#demo");
      ownsHistory.current = true;
      setDemo(true);
    };
    window.addEventListener("seero:open-demo", open);
    return () => window.removeEventListener("seero:open-demo", open);
  }, [ready]);

  return <section id="top" className={styles.hero} data-demo={demo} data-ready={ready} data-visible={visible && !failed} style={{ "--office-blur": `${ready ? 0 : Math.max(1, 18 * (1 - progress / 100))}px`, "--load-progress": `${progress}%` } as CSSProperties}
    role={demo ? "dialog" : undefined} aria-modal={demo || undefined} aria-label={demo ? "Прогулка по офису" : undefined}>
    <div className={styles.scene}>
      {/* This is the actual first frame of the office, retained when 3D is unavailable. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src="/office/poster.webp?v=20261103-7" alt="" fetchPriority="high" className={styles.poster} />
      <div ref={host} className={styles.canvas} aria-hidden={!demo} />
      <div className={styles.shade} />
      <div className={styles.grain} />
    </div>

    <div className={styles.editorial} inert={demo} aria-hidden={demo}>
      <div className={styles.eyebrow}><span /> Пространство. Ближе, чем кажется.</div>
      <h1>Быть здесь.<br /><span>Из любой точки.</span></h1>
      <div className={styles.intro}>
        <p>Сохраняем реальные пространства в 3D.<br className={styles.desktopBreak} /> Чтобы в них можно было вернуться.</p>
        <div className={styles.actions}>
          <button ref={trigger} className={styles.primary} onClick={enter} disabled={!ready} aria-busy={!ready && !failed}>
            <span>{ready ? "Войти в демо" : failed ? "Сцена недоступна" : "Готовим демо"}</span>
            {!ready && !failed ? <><span className={styles.buttonPercent}>{Math.round(progress)}%</span><span className={styles.buttonLoading} aria-hidden="true" /></> : <MoveUpRight size={20} />}
          </button>
          <a href="#contact" className={styles.projectLink}>{content.primaryLabel || "Обсудить проект"}<ArrowUpRight size={17} /></a>
        </div>
      </div>
    </div>

    <div className={styles.bottom} inert={demo} aria-hidden={demo}>
      <a href="#cases" className={styles.scrollLink}><ArrowDown size={16} /> Смотреть работы</a>
      <div className={styles.sceneCaption}><span className={styles.liveDot} /> Офис <span className={styles.divider}>/</span> {ready ? "Вы уже внутри 3D" : failed ? "Предпросмотр пространства" : "Проявляем детали"}</div>
      <div className={styles.progress} role="status" aria-live="polite">
        {failed ? <button onClick={() => { setFailed(false); setReady(false); setVisible(false); setProgress(0); setRetry(value => value + 1); }}>Повторить загрузку <RotateCcw size={13} /></button> : ready ? <button aria-label={paused ? "Продолжить движение фона" : "Остановить движение фона"} onClick={() => { office.current?.pause(!paused); setPaused(!paused); }}>{paused ? <Play size={13} /> : <Pause size={13} />}<span>{paused ? "На паузе" : "Живая сцена"}</span></button> : <><span className={styles.loadingLine}><i style={{ transform: `scaleX(${Math.max(.04, progress / 100)})` }} /></span><span>{Math.round(progress)}%</span></>}
      </div>
    </div>

    {demo && <div data-office-controls className={styles.controls}>
      <div className={styles.demoBrand}><span className={styles.liveDot} /> seero<span>.ru</span><small>Офис / свободная прогулка</small></div>
      <button ref={closeButton} className={styles.close} onClick={leave} aria-label="Закрыть демо"><X size={22} /><span>Выйти</span></button>
      <div className={styles.walkHelp}><span className={styles.keys}>W A S D</span><span>Ходить</span><i /><span>Мышь — осмотр · Esc — выйти</span></div>
      <div className={styles.mobileHelp}>Пальцем — осмотр · джойстиком — идти</div>
      <WalkJoystick onMove={(x, z) => office.current?.move(x, z)} />
      <button className={styles.reset} onClick={() => office.current?.reset()} aria-label="Вернуться к началу прогулки"><RotateCcw size={18} /></button>
    </div>}
  </section>;
}
